import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ArrowLeft, Check, X, CheckCircle2, XCircle, ImageOff, Star, AlertTriangle, Send, ClipboardCheck, ChevronRight, ChevronDown, Info, Undo2, Eye, FolderOpen, ChevronsUpDown, List, LayoutGrid } from 'lucide-react';
import { type ReviewItem, type ReviewItemStatus, initialReviewPackages, matchSourceConfig, type MatchSource } from '../data/reviewData';
type Filter = 'all' | 'pending' | 'approved' | 'rejected';
const matchExplanations: Record<MatchSource, string> = {
  exact_sku: 'Matched by exact SKU from your catalog',
  manufacturer_match: 'Matched by manufacturer product line',
  manual_match: 'Manually matched by ResVR team',
  new_entry: 'New option created for your catalog'
};
export function OptionReviewPage() {
  const {
    builderId,
    reviewId
  } = useParams<{
    builderId: string;
    reviewId: string;
  }>();
  const navigate = useNavigate();
  const pkg = initialReviewPackages.find(p => p.id === reviewId);
  const [items, setItems] = useState<ReviewItem[]>(pkg?.items || []);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Filter>('all');
  const [rejectingItemId, setRejectingItemId] = useState<string | null>(null);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectFeedback, setBulkRejectFeedback] = useState('');
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [detailItemId, setDetailItemId] = useState<string | null>(null);
  const [panelRejectFeedback, setPanelRejectFeedback] = useState('');
  const [panelRejecting, setPanelRejecting] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [activeSidebarCategory, setActiveSidebarCategory] = useState<string | null>(null);
  const detailItem = detailItemId ? items.find(i => i.id === detailItemId) || null : null;
  // ESC to close panel
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (detailItemId) {
          setDetailItemId(null);
          setPanelRejecting(false);
          setPanelRejectFeedback('');
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [detailItemId]);
  const counts = useMemo(() => {
    const approved = items.filter(i => i.status === 'approved').length;
    const rejected = items.filter(i => i.status === 'rejected').length;
    const pending = items.filter(i => i.status === 'pending').length;
    return {
      approved,
      rejected,
      pending,
      total: items.length,
      reviewed: approved + rejected
    };
  }, [items]);
  const filteredItems = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(i => i.status === filter);
  }, [items, filter]);
  // Group filtered items: subcategory → variantSet → items (two-level hierarchy)
  type SubGroup = {
    key: string;
    label: string;
    items: ReviewItem[];
  };
  type CategoryGroup = {
    key: string;
    label: string;
    subgroups: SubGroup[];
    allItems: ReviewItem[];
  };
  const groupedItems = useMemo((): CategoryGroup[] => {
    const categories: CategoryGroup[] = [];
    const catMap = new Map<string, {
      subMap: Map<string, ReviewItem[]>;
      subOrder: string[];
    }>();
    const catOrder: string[] = [];
    filteredItems.forEach(item => {
      const catKey = item.subcategory;
      if (!catMap.has(catKey)) {
        catMap.set(catKey, {
          subMap: new Map(),
          subOrder: []
        });
        catOrder.push(catKey);
      }
      const cat = catMap.get(catKey)!;
      const subKey = item.variantSet;
      if (!cat.subMap.has(subKey)) {
        cat.subMap.set(subKey, []);
        cat.subOrder.push(subKey);
      }
      cat.subMap.get(subKey)!.push(item);
    });
    catOrder.forEach(catKey => {
      const cat = catMap.get(catKey)!;
      const subgroups: SubGroup[] = [];
      const allItems: ReviewItem[] = [];
      cat.subOrder.forEach(subKey => {
        const items = cat.subMap.get(subKey)!;
        subgroups.push({
          key: subKey,
          label: subKey,
          items
        });
        allItems.push(...items);
      });
      categories.push({
        key: catKey,
        label: catKey,
        subgroups,
        allItems
      });
    });
    return categories;
  }, [filteredItems]);
  // Collect all collapsible keys for expand/collapse all
  const allSectionKeys = useMemo(() => {
    const keys: string[] = [];
    groupedItems.forEach(group => {
      keys.push(group.key);
      if (group.subgroups.length > 1) {
        group.subgroups.forEach(sub => {
          keys.push(`sub::${group.key}::${sub.key}`);
        });
      }
    });
    return keys;
  }, [groupedItems]);
  const allCollapsed = allSectionKeys.length > 0 && allSectionKeys.every(k => collapsedSections.has(k));
  // When a category is selected, filter to that category
  const displayedGroups = useMemo(() => {
    if (!activeSidebarCategory) return groupedItems;
    return groupedItems.filter(g => g.key === activeSidebarCategory);
  }, [groupedItems, activeSidebarCategory]);
  const handleExpandCollapseAll = () => {
    if (allCollapsed) {
      setCollapsedSections(new Set());
    } else {
      setCollapsedSections(new Set(allSectionKeys));
    }
  };
  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every(i => selectedIds.has(i.id));
  const toggleSection = (key: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);else next.add(key);
      return next;
    });
  };
  const toggleSubSection = (parentKey: string, subKey: string) => {
    const compositeKey = `sub::${parentKey}::${subKey}`;
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(compositeKey)) next.delete(compositeKey);else next.add(compositeKey);
      return next;
    });
  };
  const getSectionCounts = (sectionItems: ReviewItem[]) => {
    const approved = sectionItems.filter(i => i.status === 'approved').length;
    const rejected = sectionItems.filter(i => i.status === 'rejected').length;
    const pending = sectionItems.filter(i => i.status === 'pending').length;
    return {
      approved,
      rejected,
      pending,
      total: sectionItems.length
    };
  };
  const handleSelectSection = (sectionItems: ReviewItem[]) => {
    const sectionIds = sectionItems.map(i => i.id);
    const allSelected = sectionIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        sectionIds.forEach(id => next.delete(id));
      } else {
        sectionIds.forEach(id => next.add(id));
      }
      return next;
    });
  };
  const updateItem = (id: string, updates: Partial<ReviewItem>) => {
    setItems(prev => prev.map(i => i.id === id ? {
      ...i,
      ...updates
    } : i));
  };
  const handleApprove = (id: string) => {
    updateItem(id, {
      status: 'approved',
      feedback: undefined
    });
    setRejectingItemId(null);
  };
  const handleConfirmReject = (id: string) => {
    if (!rejectFeedback.trim()) return;
    updateItem(id, {
      status: 'rejected',
      feedback: rejectFeedback.trim()
    });
    setRejectingItemId(null);
    setRejectFeedback('');
  };
  const handlePanelReject = () => {
    if (!panelRejectFeedback.trim() || !detailItemId) return;
    updateItem(detailItemId, {
      status: 'rejected',
      feedback: panelRejectFeedback.trim()
    });
    setPanelRejecting(false);
    setPanelRejectFeedback('');
  };
  const handleUndo = (id: string) => {
    updateItem(id, {
      status: 'pending',
      feedback: undefined
    });
    setPanelRejecting(false);
    setPanelRejectFeedback('');
  };
  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const handleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredItems.forEach(i => next.delete(i.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredItems.forEach(i => next.add(i.id));
        return next;
      });
    }
  };
  const handleBulkApprove = () => {
    setItems(prev => prev.map(i => selectedIds.has(i.id) && i.status === 'pending' ? {
      ...i,
      status: 'approved' as const
    } : i));
    setSelectedIds(new Set());
  };
  const handleBulkRejectConfirm = () => {
    if (!bulkRejectFeedback.trim()) return;
    setItems(prev => prev.map(i => selectedIds.has(i.id) && i.status === 'pending' ? {
      ...i,
      status: 'rejected' as const,
      feedback: bulkRejectFeedback.trim()
    } : i));
    setSelectedIds(new Set());
    setBulkRejectOpen(false);
    setBulkRejectFeedback('');
  };
  const handleSubmit = () => {
    setSubmitted(true);
    setSubmitModalOpen(false);
  };
  const selectedPendingCount = [...selectedIds].filter(id => items.find(i => i.id === id)?.status === 'pending').length;
  if (!pkg) {
    return <div className="flex flex-col items-center justify-center py-24" data-id="element-5656">
        <AlertTriangle className="h-10 w-10 text-slate-300 mb-3" data-id="element-5657" />
        <p className="text-slate-500 font-medium" data-id="element-5658">Review package not found</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate(`/${builderId}/options`)} data-id="element-5659">
          Back to Options
        </Button>
      </div>;
  }
  if (submitted) {
    return <div className="flex flex-col items-center justify-center py-24 max-w-md mx-auto text-center" data-id="element-5660">
        <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5" data-id="element-5661">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" data-id="element-5662" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-5663">
          Review Submitted
        </h1>
        <p className="text-slate-500 mb-6" data-id="element-5664">
          Your review for "{pkg.catalogName}" has been submitted to ResVR.
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 w-full mb-6 space-y-2" data-id="element-5665">
          <div className="flex items-center justify-between text-sm" data-id="element-5666">
            <span className="text-slate-500" data-id="element-5667">Work Order</span>
            <span className="font-mono font-semibold text-slate-900" data-id="element-5668">
              WO-2026-0218
            </span>
          </div>
          <div className="flex items-center justify-between text-sm" data-id="element-5669">
            <span className="text-slate-500" data-id="element-5670">Approved</span>
            <span className="font-semibold text-emerald-600" data-id="element-5671">
              {counts.approved} options
            </span>
          </div>
          <div className="flex items-center justify-between text-sm" data-id="element-5672">
            <span className="text-slate-500" data-id="element-5673">Rejected</span>
            <span className="font-semibold text-red-600" data-id="element-5674">
              {counts.rejected} options
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-6" data-id="element-5675">
          {counts.rejected > 0 ? 'Rejected items have been sent back to ResVR with your feedback. Approved items will be rendered.' : 'All approved items will be rendered by the ResVR team.'}
        </p>
        <Button onClick={() => navigate(`/${builderId}/options`)} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-5677" />} data-id="element-5676">
          Back to Options
        </Button>
      </div>;
  }
  const approvedPercent = counts.total > 0 ? counts.approved / counts.total * 100 : 0;
  const rejectedPercent = counts.total > 0 ? counts.rejected / counts.total * 100 : 0;
  const progressPercent = counts.total > 0 ? counts.reviewed / counts.total * 100 : 0;
  return <div className="flex flex-col h-[calc(100vh-8rem)]" data-id="element-5678">
      {/* Bulk Reject Modal */}
      {bulkRejectOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-5679">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" data-id="element-5680">
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-5681">
              Reject Selected Options
            </h3>
            <p className="text-sm text-slate-500 mb-4" data-id="element-5682">
              This feedback will apply to {selectedPendingCount} selected item
              {selectedPendingCount !== 1 ? 's' : ''}.
            </p>
            <textarea value={bulkRejectFeedback} onChange={e => setBulkRejectFeedback(e.target.value)} rows={3} placeholder="Why are these options being rejected?" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500 mb-4" autoFocus data-id="element-5683" />
            <div className="flex justify-end gap-3" data-id="element-5684">
              <Button variant="ghost" size="sm" onClick={() => {
            setBulkRejectOpen(false);
            setBulkRejectFeedback('');
          }} data-id="element-5685">
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleBulkRejectConfirm} disabled={!bulkRejectFeedback.trim()} leftIcon={<XCircle className="h-4 w-4" data-id="element-5687" />} data-id="element-5686">
                Reject {selectedPendingCount} Items
              </Button>
            </div>
          </div>
        </div>}

      {/* Submit Confirmation Modal */}
      {submitModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-5688">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" data-id="element-5689">
            <div className="flex items-center gap-3 mb-4" data-id="element-5690">
              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0" data-id="element-5691">
                <Send className="h-5 w-5 text-primary-600" data-id="element-5692" />
              </div>
              <div data-id="element-5693">
                <h3 className="text-lg font-semibold text-slate-900" data-id="element-5694">
                  Submit Review
                </h3>
                <p className="text-sm text-slate-500" data-id="element-5695">{pkg.catalogName}</p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 space-y-1.5" data-id="element-5696">
              <div className="flex items-center justify-between text-sm" data-id="element-5697">
                <span className="flex items-center gap-1.5 text-emerald-700" data-id="element-5698">
                  <CheckCircle2 className="h-3.5 w-3.5" data-id="element-5699" /> Approved
                </span>
                <span className="font-semibold" data-id="element-5700">{counts.approved}</span>
              </div>
              <div className="flex items-center justify-between text-sm" data-id="element-5701">
                <span className="flex items-center gap-1.5 text-red-700" data-id="element-5702">
                  <XCircle className="h-3.5 w-3.5" data-id="element-5703" /> Rejected
                </span>
                <span className="font-semibold" data-id="element-5704">{counts.rejected}</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-1" data-id="element-5705">
              This will create a work order for ResVR to render all approved
              options.
            </p>
            {counts.rejected > 0 && <p className="text-xs text-amber-600" data-id="element-5706">
                Rejected items will be sent back with your feedback for
                revision.
              </p>}
            <div className="flex justify-end gap-3 mt-5" data-id="element-5707">
              <Button variant="ghost" size="sm" onClick={() => setSubmitModalOpen(false)} data-id="element-5708">
                Cancel
              </Button>
              <Button size="sm" onClick={handleSubmit} leftIcon={<Send className="h-4 w-4" data-id="element-5710" />} data-id="element-5709">
                Submit Review
              </Button>
            </div>
          </div>
        </div>}

      {/* Slide-out Detail Panel */}
      {detailItemId && <>
          <div className="fixed inset-0 bg-black/30 z-40 transition-opacity" onClick={() => {
        setDetailItemId(null);
        setPanelRejecting(false);
        setPanelRejectFeedback('');
      }} data-id="element-5711" />
          <div className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl border-l border-slate-200 z-50 flex flex-col overflow-hidden animate-slide-in-right" data-id="element-5712">
            {detailItem && (() => {
          const mc = matchSourceConfig[detailItem.matchSource];
          return <>
                    {/* Panel header */}
                    <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0" data-id="element-5713">
                      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-5714">
                        Option Details
                      </span>
                      <button onClick={() => {
                setDetailItemId(null);
                setPanelRejecting(false);
                setPanelRejectFeedback('');
              }} className="p-1 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-600 transition-colors" data-id="element-5715">
                        <X className="h-5 w-5" data-id="element-5716" />
                      </button>
                    </div>

                    {/* Scrollable content */}
                    <div className="flex-1 overflow-y-auto" data-id="element-5717">
                      {/* Image */}
                      <div className="h-56 bg-slate-100 flex items-center justify-center overflow-hidden" data-id="element-5718">
                        {detailItem.hasImage && detailItem.imageUrl ? <img src={detailItem.imageUrl} alt={detailItem.optionName} className="h-full w-full object-cover" data-id="element-5719" /> : <div className="flex flex-col items-center gap-2 text-slate-300" data-id="element-5720">
                            <ImageOff className="h-12 w-12" data-id="element-5721" />
                            <span className="text-xs" data-id="element-5722">No image available</span>
                          </div>}
                      </div>

                      <div className="p-5 space-y-5" data-id="element-5723">
                        {/* Name + match badge */}
                        <div data-id="element-5724">
                          <h2 className="text-lg font-bold text-slate-900 mb-2" data-id="element-5725">
                            {detailItem.optionName}
                          </h2>
                          <div className="flex items-start gap-2" data-id="element-5726">
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${mc.bgColor} ${mc.color} ${mc.borderColor}`} data-id="element-5727">
                              {mc.label}
                            </span>
                            <span className="text-xs text-slate-500" data-id="element-5728">
                              {matchExplanations[detailItem.matchSource]}
                            </span>
                          </div>
                        </div>

                        {/* Description */}
                        <div data-id="element-5729">
                          <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1" data-id="element-5730">
                            Description
                          </h4>
                          <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5731">
                            {detailItem.description}
                          </p>
                        </div>

                        {/* Details grid */}
                        <div data-id="element-5732">
                          <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-5733">
                            Details
                          </h4>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5" data-id="element-5734">
                            <div data-id="element-5735">
                              <p className="text-[11px] text-slate-400" data-id="element-5736">
                                Category
                              </p>
                              <p className="text-sm font-medium text-slate-800" data-id="element-5737">
                                {detailItem.category}
                              </p>
                            </div>
                            <div data-id="element-5738">
                              <p className="text-[11px] text-slate-400" data-id="element-5739">
                                Subcategory
                              </p>
                              <p className="text-sm font-medium text-slate-800" data-id="element-5740">
                                {detailItem.subcategory}
                              </p>
                            </div>
                            <div data-id="element-5741">
                              <p className="text-[11px] text-slate-400" data-id="element-5742">
                                Variant Set
                              </p>
                              <p className="text-sm font-medium text-slate-800" data-id="element-5743">
                                {detailItem.variantSet}
                              </p>
                            </div>
                            {detailItem.manufacturerName && <div data-id="element-5744">
                                <p className="text-[11px] text-slate-400" data-id="element-5745">
                                  Manufacturer
                                </p>
                                <p className="text-sm font-medium text-slate-800" data-id="element-5746">
                                  {detailItem.manufacturerName}
                                </p>
                              </div>}
                            {detailItem.sku && <div data-id="element-5747">
                                <p className="text-[11px] text-slate-400" data-id="element-5748">
                                  SKU
                                </p>
                                <p className="text-sm font-mono text-slate-800" data-id="element-5749">
                                  {detailItem.sku}
                                </p>
                              </div>}
                          </div>
                        </div>

                        {/* Price */}
                        <div data-id="element-5750">
                          {detailItem.isStandard ? <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-id="element-5751">
                              <Star className="h-4 w-4 text-emerald-600" data-id="element-5752" />
                              <span className="text-sm font-medium text-emerald-700" data-id="element-5753">
                                Included as Standard
                              </span>
                            </div> : <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-id="element-5754">
                              <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-5755">
                                Upgrade Price
                              </p>
                              <p className="text-2xl font-bold text-slate-900" data-id="element-5756">
                                ${detailItem.price.toLocaleString()}
                              </p>
                            </div>}
                        </div>
                      </div>
                    </div>

                    {/* Panel footer — review actions */}
                    <div className="flex-shrink-0 border-t border-slate-200 p-4 space-y-2 bg-white" data-id="element-5757">
                      {detailItem.status === 'pending' && !panelRejecting && <>
                          <button onClick={() => handleApprove(detailItem.id)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors" data-id="element-5758">
                            <Check className="h-4 w-4" data-id="element-5759" /> Approve Option
                          </button>
                          <button onClick={() => setPanelRejecting(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors" data-id="element-5760">
                            <X className="h-4 w-4" data-id="element-5761" /> Reject Option
                          </button>
                        </>}
                      {detailItem.status === 'pending' && panelRejecting && <div className="space-y-2" data-id="element-5762">
                          <label className="block text-xs font-medium text-red-800" data-id="element-5763">
                            Why are you rejecting this option?
                          </label>
                          <textarea value={panelRejectFeedback} onChange={e => setPanelRejectFeedback(e.target.value)} rows={3} placeholder="Required — describe what needs to change..." className="w-full px-3 py-2 border border-red-200 rounded-md text-sm focus:ring-red-500 focus:border-red-500" autoFocus data-id="element-5764" />
                          <div className="flex gap-2" data-id="element-5765">
                            <Button variant="ghost" size="sm" className="flex-1" onClick={() => {
                    setPanelRejecting(false);
                    setPanelRejectFeedback('');
                  }} data-id="element-5766">
                              Cancel
                            </Button>
                            <Button variant="danger" size="sm" className="flex-1" onClick={handlePanelReject} disabled={!panelRejectFeedback.trim()} leftIcon={<XCircle className="h-3.5 w-3.5" data-id="element-5768" />} data-id="element-5767">
                              Confirm Rejection
                            </Button>
                          </div>
                        </div>}
                      {detailItem.status === 'approved' && <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between" data-id="element-5769">
                          <span className="flex items-center gap-2 text-sm font-medium text-emerald-700" data-id="element-5770">
                            <CheckCircle2 className="h-4 w-4" data-id="element-5771" /> Approved
                          </span>
                          <button onClick={() => handleUndo(detailItem.id)} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-5772">
                            Undo
                          </button>
                        </div>}
                      {detailItem.status === 'rejected' && <div className="space-y-2" data-id="element-5773">
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-5774">
                            <div className="flex items-center justify-between mb-1" data-id="element-5775">
                              <span className="flex items-center gap-2 text-sm font-medium text-red-700" data-id="element-5776">
                                <XCircle className="h-4 w-4" data-id="element-5777" /> Rejected
                              </span>
                              <button onClick={() => handleUndo(detailItem.id)} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-5778">
                                Undo
                              </button>
                            </div>
                            {detailItem.feedback && <p className="text-xs text-red-600 mt-1" data-id="element-5779">
                                {detailItem.feedback}
                              </p>}
                          </div>
                        </div>}
                    </div>
                  </>;
        })()}
          </div>
        </>}

      {/* Header */}
      <div className="flex-shrink-0 space-y-4 mb-4" data-id="element-5780">
        <div className="flex items-center gap-3" data-id="element-5781">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/${builderId}/options`)} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-5783" />} data-id="element-5782">
            Back
          </Button>
          <div className="flex-1" data-id="element-5784">
            <div className="flex items-center gap-3" data-id="element-5785">
              <ClipboardCheck className="h-5 w-5 text-primary-500" data-id="element-5786" />
              <h1 className="text-xl font-bold text-slate-900" data-id="element-5787">
                {pkg.catalogName}
              </h1>
            </div>
            <p className="text-sm text-slate-500 ml-8" data-id="element-5788">
              Sent by ResVR
              {pkg.sentAt ? ` · ${new Date(pkg.sentAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}` : ''}
              {pkg.dueDate ? ` · Due ${new Date(pkg.dueDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}` : ''}
            </p>
          </div>
        </div>

        {pkg.notes && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-id="element-5789">
            <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-5790" />
            <p className="text-sm text-blue-800" data-id="element-5791">{pkg.notes}</p>
          </div>}

        {/* Progress bar */}
        <div data-id="element-5792">
          <div className="flex items-center justify-between mb-1.5" data-id="element-5793">
            <span className="text-sm font-medium text-slate-700" data-id="element-5794">
              {counts.reviewed} of {counts.total} reviewed
            </span>
            <span className="text-xs text-slate-400" data-id="element-5795">
              {Math.round(progressPercent)}%
            </span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden flex" data-id="element-5796">
            {approvedPercent > 0 && <div className="bg-emerald-500 transition-all duration-500 ease-out" style={{
            width: `${approvedPercent}%`
          }} data-id="element-5797" />}
            {rejectedPercent > 0 && <div className="bg-red-400 transition-all duration-500 ease-out" style={{
            width: `${rejectedPercent}%`
          }} data-id="element-5798" />}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500" data-id="element-5799">
            <span className="flex items-center gap-1" data-id="element-5800">
              <span className="h-2 w-2 rounded-full bg-emerald-500" data-id="element-5801" />{' '}
              {counts.approved} approved
            </span>
            <span className="flex items-center gap-1" data-id="element-5802">
              <span className="h-2 w-2 rounded-full bg-red-400" data-id="element-5803" />{' '}
              {counts.rejected} rejected
            </span>
            <span className="flex items-center gap-1" data-id="element-5804">
              <span className="h-2 w-2 rounded-full bg-slate-200" data-id="element-5805" />{' '}
              {counts.pending} pending
            </span>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex-shrink-0 bg-white border border-slate-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-4 shadow-sm sticky top-0 z-10" data-id="element-5806">
        <label className="flex items-center gap-2 cursor-pointer flex-shrink-0" data-id="element-5807">
          <input type="checkbox" checked={allFilteredSelected} onChange={handleSelectAll} className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" data-id="element-5808" />
          <span className="text-xs text-slate-500" data-id="element-5809">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
          </span>
        </label>
        <div className="flex-1 flex items-center justify-center" data-id="element-5810">
          <div className="flex bg-slate-100 rounded-lg p-0.5" data-id="element-5811">
            {(['all', 'pending', 'approved', 'rejected'] as Filter[]).map(f => {
            const count = f === 'all' ? counts.total : counts[f];
            return <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5812">
                    {f.charAt(0).toUpperCase() + f.slice(1)}{' '}
                    <span className="ml-1 text-slate-400" data-id="element-5813">{count}</span>
                  </button>;
          })}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-5814">
          <div className="flex border border-slate-200 rounded-md overflow-hidden" data-id="element-5815">
            <button onClick={() => setViewMode('list')} className={`p-1.5 transition-colors ${viewMode === 'list' ? 'bg-slate-100 text-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`} title="List view" data-id="element-5816">
              <List className="h-4 w-4" data-id="element-5817" />
            </button>
            <button onClick={() => setViewMode('grid')} className={`p-1.5 transition-colors border-l border-slate-200 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`} title="Grid view" data-id="element-5818">
              <LayoutGrid className="h-4 w-4" data-id="element-5819" />
            </button>
          </div>
          <button onClick={handleExpandCollapseAll} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors border border-slate-200" title={allCollapsed ? 'Expand all folders' : 'Collapse all folders'} data-id="element-5820">
            <ChevronsUpDown className="h-3.5 w-3.5" data-id="element-5821" />
            {allCollapsed ? 'Expand All' : 'Collapse All'}
          </button>
          {selectedIds.size > 0 && selectedPendingCount > 0 && <>
              <Button size="sm" variant="outline" onClick={handleBulkApprove} leftIcon={<Check className="h-3.5 w-3.5" data-id="element-5823" />} className="!text-emerald-700 !border-emerald-300 hover:!bg-emerald-50" data-id="element-5822">
                Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => setBulkRejectOpen(true)} leftIcon={<X className="h-3.5 w-3.5" data-id="element-5825" />} className="!text-red-700 !border-red-300 hover:!bg-red-50" data-id="element-5824">
                Reject
              </Button>
            </>}
        </div>
      </div>

      {/* Items list — grouped by category → subcategory */}
      <div className="flex-1 overflow-hidden flex gap-0" data-id="element-5826">
        {/* Category Sidebar */}
        <div className="w-56 flex-shrink-0 border-r border-slate-200 bg-slate-50/50 overflow-y-auto py-3 px-2" data-id="element-5827">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2" data-id="element-5828">
            Categories
          </p>
          {/* All Categories */}
          <button onClick={() => setActiveSidebarCategory(null)} className={`w-full flex items-center justify-between px-2.5 py-2 rounded-md text-sm transition-colors mb-0.5 ${!activeSidebarCategory ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-5829">
            <span className="flex items-center gap-2" data-id="element-5830">
              <FolderOpen className="h-3.5 w-3.5" data-id="element-5831" />
              All Categories
            </span>
            <span className={`text-xs ${!activeSidebarCategory ? 'text-primary-500' : 'text-slate-400'}`} data-id="element-5832">
              {filteredItems.length}
            </span>
          </button>

          <div className="h-px bg-slate-200 my-2" data-id="element-5833" />

          {/* Category list */}
          {groupedItems.map(group => {
          const isActive = activeSidebarCategory === group.key;
          const sCounts = getSectionCounts(group.allItems);
          return <div key={group.key} className="mb-0.5" data-id="element-5834">
                <button onClick={() => setActiveSidebarCategory(isActive ? null : group.key)} className={`w-full flex items-center justify-between px-2.5 py-2 rounded-md text-sm transition-colors ${isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-5835">
                  <span className="flex items-center gap-2 truncate" data-id="element-5836">
                    <FolderOpen className={`h-3.5 w-3.5 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-slate-400'}`} data-id="element-5837" />
                    <span className="truncate" data-id="element-5838">{group.label}</span>
                  </span>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-1" data-id="element-5839">
                    {sCounts.approved > 0 && <span className="text-[10px] font-medium text-emerald-600" data-id="element-5840">
                        {sCounts.approved}
                      </span>}
                    {sCounts.rejected > 0 && <span className="text-[10px] font-medium text-red-500" data-id="element-5841">
                        {sCounts.rejected}
                      </span>}
                    <span className={`text-xs ${isActive ? 'text-primary-500' : 'text-slate-400'}`} data-id="element-5842">
                      {sCounts.total}
                    </span>
                  </div>
                </button>
                {/* Show subcategories when active */}
                {isActive && group.subgroups.length > 1 && <div className="ml-5 mt-0.5 space-y-0.5" data-id="element-5843">
                    {group.subgroups.map(sub => {
                const subC = getSectionCounts(sub.items);
                return <div key={sub.key} className="flex items-center justify-between px-2.5 py-1.5 text-xs text-slate-500" data-id="element-5844">
                          <span className="truncate" data-id="element-5845">{sub.label}</span>
                          <span className="text-slate-400 flex-shrink-0" data-id="element-5846">
                            {subC.total}
                          </span>
                        </div>;
              })}
                  </div>}
                {/* Progress bar per category */}
                {sCounts.total > 0 && (sCounts.approved > 0 || sCounts.rejected > 0) && <div className="mx-2.5 mt-1 mb-1.5 h-1 bg-slate-200 rounded-full overflow-hidden flex" data-id="element-5847">
                      {sCounts.approved > 0 && <div className="bg-emerald-400" style={{
                width: `${sCounts.approved / sCounts.total * 100}%`
              }} data-id="element-5848" />}
                      {sCounts.rejected > 0 && <div className="bg-red-400" style={{
                width: `${sCounts.rejected / sCounts.total * 100}%`
              }} data-id="element-5849" />}
                    </div>}
              </div>;
        })}
        </div>

        {/* Main content area */}
        <div className="flex-1 overflow-y-auto space-y-5 pb-24 pl-4" data-id="element-5850">
          {displayedGroups.map(group => {
          const isCollapsed = collapsedSections.has(group.key);
          const sectionCounts = getSectionCounts(group.allItems);
          const sectionAllSelected = group.allItems.every(i => selectedIds.has(i.id));
          const sectionSomeSelected = group.allItems.some(i => selectedIds.has(i.id));
          return <div key={group.key} data-id="element-5851">
                {/* Category header */}
                <div className="flex items-center gap-2 mb-2" data-id="element-5852">
                  <label className="flex items-center flex-shrink-0 cursor-pointer" onClick={e => e.stopPropagation()} data-id="element-5853">
                    <input type="checkbox" checked={sectionAllSelected} ref={el => {
                  if (el) el.indeterminate = sectionSomeSelected && !sectionAllSelected;
                }} onChange={() => handleSelectSection(group.allItems)} className="h-3.5 w-3.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500" data-id="element-5854" />
                  </label>
                  <button onClick={() => toggleSection(group.key)} className="flex items-center gap-2 flex-1 group" data-id="element-5855">
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} data-id="element-5856" />
                    <FolderOpen className="h-4 w-4 text-slate-400" data-id="element-5857" />
                    <span className="text-sm font-semibold text-slate-700" data-id="element-5858">
                      {group.label}
                    </span>
                    <span className="text-xs text-slate-400" data-id="element-5859">
                      {sectionCounts.total} option
                      {sectionCounts.total !== 1 ? 's' : ''}
                    </span>
                  </button>
                  {/* Section mini-progress */}
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-5860">
                    {sectionCounts.approved > 0 && <span className="flex items-center gap-0.5 text-[10px] font-medium text-emerald-600" data-id="element-5861">
                        <CheckCircle2 className="h-3 w-3" data-id="element-5862" />{' '}
                        {sectionCounts.approved}
                      </span>}
                    {sectionCounts.rejected > 0 && <span className="flex items-center gap-0.5 text-[10px] font-medium text-red-500" data-id="element-5863">
                        <XCircle className="h-3 w-3" data-id="element-5864" /> {sectionCounts.rejected}
                      </span>}
                    {sectionCounts.pending > 0 && <span className="text-[10px] text-slate-400" data-id="element-5865">
                        {sectionCounts.pending} pending
                      </span>}
                    {sectionCounts.pending === 0 && sectionCounts.total > 0 && <span className="text-[10px] text-emerald-500 font-medium" data-id="element-5866">
                        ✓ Done
                      </span>}
                  </div>
                </div>

                {/* Expanded: subcategory groups */}
                {!isCollapsed && <div className="ml-6 space-y-4" data-id="element-5867">
                    {group.subgroups.map(sub => {
                const showSubHeader = group.subgroups.length > 1;
                const subCompositeKey = `sub::${group.key}::${sub.key}`;
                const isSubCollapsed = collapsedSections.has(subCompositeKey);
                const subCounts = getSectionCounts(sub.items);
                return <div key={sub.key} data-id="element-5868">
                          {/* Subcategory label — collapsible */}
                          {showSubHeader && <button onClick={() => toggleSubSection(group.key, sub.key)} className="flex items-center gap-2 mb-1.5 ml-1 w-full group" data-id="element-5869">
                              <ChevronDown className={`h-3 w-3 text-slate-300 transition-transform ${isSubCollapsed ? '-rotate-90' : ''}`} data-id="element-5870" />
                              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider group-hover:text-slate-600 transition-colors" data-id="element-5871">
                                {sub.label}
                              </span>
                              <span className="text-[10px] text-slate-300" data-id="element-5872">
                                ({sub.items.length})
                              </span>
                              <div className="h-px flex-1 bg-slate-200" data-id="element-5873" />
                              {/* Sub-section mini status */}
                              <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-5874">
                                {subCounts.approved > 0 && <span className="flex items-center gap-0.5 text-[10px] font-medium text-emerald-600" data-id="element-5875">
                                    <CheckCircle2 className="h-2.5 w-2.5" data-id="element-5876" />{' '}
                                    {subCounts.approved}
                                  </span>}
                                {subCounts.rejected > 0 && <span className="flex items-center gap-0.5 text-[10px] font-medium text-red-500" data-id="element-5877">
                                    <XCircle className="h-2.5 w-2.5" data-id="element-5878" />{' '}
                                    {subCounts.rejected}
                                  </span>}
                                {subCounts.pending > 0 && <span className="text-[10px] text-slate-300" data-id="element-5879">
                                    {subCounts.pending} pending
                                  </span>}
                                {subCounts.pending === 0 && subCounts.total > 0 && <span className="text-[10px] text-emerald-500 font-medium" data-id="element-5880">
                                      ✓
                                    </span>}
                              </div>
                            </button>}

                          {/* Items in this subcategory */}
                          {(!showSubHeader || !isSubCollapsed) && <>
                              {viewMode === 'list' ? <div className="space-y-2" data-id="element-5881">
                                  {sub.items.map(item => {
                        const matchConfig = matchSourceConfig[item.matchSource];
                        const isRejecting = rejectingItemId === item.id;
                        return <div key={item.id} data-id="element-5882">
                                        <div className={`bg-white border rounded-lg p-4 transition-all ${item.status === 'approved' ? 'border-l-4 border-l-emerald-400 border-t-slate-200 border-r-slate-200 border-b-slate-200' : item.status === 'rejected' ? 'border-l-4 border-l-red-400 border-t-slate-200 border-r-slate-200 border-b-slate-200' : 'border-slate-200'}`} data-id="element-5883">
                                          <div className="flex items-start gap-3" data-id="element-5884">
                                            {/* Checkbox */}
                                            <div className="pt-1 flex-shrink-0" data-id="element-5885">
                                              <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => handleToggleSelect(item.id)} className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" data-id="element-5886" />
                                            </div>

                                            {/* Image — clickable */}
                                            <button onClick={() => {
                                setDetailItemId(item.id);
                                setPanelRejecting(false);
                                setPanelRejectFeedback('');
                              }} className="h-14 w-14 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-primary-300 transition-all cursor-pointer" data-id="element-5887">
                                              {item.hasImage && item.imageUrl ? <img src={item.imageUrl} alt="" className="h-full w-full object-cover" data-id="element-5888" /> : <ImageOff className="h-5 w-5 text-slate-300" data-id="element-5889" />}
                                            </button>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0" data-id="element-5890">
                                              <div className="flex items-center gap-2 mb-0.5 flex-wrap" data-id="element-5891">
                                                <button onClick={() => {
                                    setDetailItemId(item.id);
                                    setPanelRejecting(false);
                                    setPanelRejectFeedback('');
                                  }} className="font-medium text-slate-900 text-sm hover:text-primary-600 transition-colors cursor-pointer" data-id="element-5892">
                                                  {item.optionName}
                                                </button>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${matchConfig.bgColor} ${matchConfig.color} ${matchConfig.borderColor}`} data-id="element-5893">
                                                  {matchConfig.label}
                                                </span>
                                                {item.isStandard && <Badge variant="success" className="text-[10px] px-1.5 py-0.5" data-id="element-5894">
                                                    <Star className="h-2.5 w-2.5 mr-0.5" data-id="element-5895" />{' '}
                                                    Included
                                                  </Badge>}
                                              </div>
                                              <p className="text-xs text-slate-500 truncate mb-1" data-id="element-5896">
                                                {item.description}
                                              </p>
                                              <div className="flex items-center gap-1 text-[11px] text-slate-400 flex-wrap" data-id="element-5897">
                                                {item.manufacturerName && <span data-id="element-5898">
                                                    {item.manufacturerName}
                                                    {item.sku ? ` (${item.sku})` : ''}
                                                  </span>}
                                              </div>
                                            </div>

                                            {/* Right section: price + actions stacked */}
                                            <div className="flex-shrink-0 flex flex-col items-end gap-2 min-w-[180px]" data-id="element-5899">
                                              <div className="text-right" data-id="element-5900">
                                                {item.isStandard ? <span className="text-xs font-medium text-emerald-600" data-id="element-5901">
                                                    Standard
                                                  </span> : <span className="text-sm font-semibold text-slate-900" data-id="element-5902">
                                                    $
                                                    {item.price.toLocaleString()}
                                                  </span>}
                                              </div>
                                              <div className="flex items-center gap-2" data-id="element-5903">
                                                {item.status === 'pending' && <>
                                                    <button onClick={() => handleApprove(item.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors" data-id="element-5904">
                                                      <Check className="h-3.5 w-3.5" data-id="element-5905" />{' '}
                                                      Approve
                                                    </button>
                                                    <button onClick={() => {
                                      setRejectingItemId(item.id);
                                      setRejectFeedback('');
                                    }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors" data-id="element-5906">
                                                      <X className="h-3.5 w-3.5" data-id="element-5907" />{' '}
                                                      Reject
                                                    </button>
                                                  </>}
                                                {item.status === 'approved' && <>
                                                    <span className="flex items-center gap-1 text-xs font-medium text-emerald-600" data-id="element-5908">
                                                      <CheckCircle2 className="h-4 w-4" data-id="element-5909" />{' '}
                                                      Approved
                                                    </span>
                                                    <button onClick={() => handleUndo(item.id)} className="text-[11px] text-slate-400 hover:text-slate-600 underline" data-id="element-5910">
                                                      Undo
                                                    </button>
                                                  </>}
                                                {item.status === 'rejected' && <>
                                                    <span className="flex items-center gap-1 text-xs font-medium text-red-600" data-id="element-5911">
                                                      <XCircle className="h-4 w-4" data-id="element-5912" />{' '}
                                                      Rejected
                                                    </span>
                                                    <button onClick={() => handleUndo(item.id)} className="text-[11px] text-slate-400 hover:text-slate-600 underline" data-id="element-5913">
                                                      Undo
                                                    </button>
                                                  </>}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Rejected feedback display */}
                                        {item.status === 'rejected' && item.feedback && !isRejecting && <div className="ml-10 mr-4 mt-1 mb-1 pl-4 border-l-2 border-red-200 py-1.5" data-id="element-5914">
                                              <p className="text-xs text-red-700" data-id="element-5915">
                                                <span className="font-medium" data-id="element-5916">
                                                  Feedback:
                                                </span>{' '}
                                                {item.feedback}
                                              </p>
                                            </div>}

                                        {/* Inline reject form */}
                                        {isRejecting && <div className="ml-10 mr-4 mt-1 mb-1 bg-red-50/50 border border-red-200 rounded-md p-3 space-y-2" data-id="element-5917">
                                            <label className="block text-xs font-medium text-red-800" data-id="element-5918">
                                              Why are you rejecting this option?
                                            </label>
                                            <textarea value={rejectFeedback} onChange={e => setRejectFeedback(e.target.value)} rows={2} placeholder="Required — describe what needs to change..." className="w-full px-3 py-2 border border-red-200 rounded-md text-sm focus:ring-red-500 focus:border-red-500 bg-white" autoFocus data-id="element-5919" />
                                            <div className="flex justify-end gap-2" data-id="element-5920">
                                              <Button variant="ghost" size="sm" onClick={() => {
                                setRejectingItemId(null);
                                setRejectFeedback('');
                              }} data-id="element-5921">
                                                Cancel
                                              </Button>
                                              <Button variant="danger" size="sm" onClick={() => handleConfirmReject(item.id)} disabled={!rejectFeedback.trim()} leftIcon={<XCircle className="h-3.5 w-3.5" data-id="element-5923" />} data-id="element-5922">
                                                Confirm Rejection
                                              </Button>
                                            </div>
                                          </div>}
                                      </div>;
                      })}
                                </div> /* Grid / Card View */ : <div className="grid grid-cols-2 xl:grid-cols-3 gap-3" data-id="element-5924">
                                  {sub.items.map(item => {
                        const matchConfig = matchSourceConfig[item.matchSource];
                        return <div key={item.id} className={`bg-white border rounded-lg overflow-hidden flex flex-col transition-all ${item.status === 'approved' ? 'border-l-4 border-l-emerald-400 border-t-slate-200 border-r-slate-200 border-b-slate-200' : item.status === 'rejected' ? 'border-l-4 border-l-red-400 border-t-slate-200 border-r-slate-200 border-b-slate-200' : 'border-slate-200'}`} data-id="element-5925">
                                        {/* Image area */}
                                        <button onClick={() => {
                            setDetailItemId(item.id);
                            setPanelRejecting(false);
                            setPanelRejectFeedback('');
                          }} className="h-36 bg-slate-50 flex items-center justify-center overflow-hidden relative group cursor-pointer" data-id="element-5926">
                                          {item.hasImage && item.imageUrl ? <img src={item.imageUrl} alt={item.optionName} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-5927" /> : <div className="flex flex-col items-center gap-1.5 text-slate-300" data-id="element-5928">
                                              <ImageOff className="h-8 w-8" data-id="element-5929" />
                                              <span className="text-[10px]" data-id="element-5930">
                                                No image
                                              </span>
                                            </div>}
                                          {/* Checkbox overlay */}
                                          <div className="absolute top-2 left-2" onClick={e => e.stopPropagation()} data-id="element-5931">
                                            <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => handleToggleSelect(item.id)} className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 bg-white/90 shadow-sm" data-id="element-5932" />
                                          </div>
                                          {/* Match badge overlay */}
                                          <div className="absolute top-2 right-2" data-id="element-5933">
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium border shadow-sm ${matchConfig.bgColor} ${matchConfig.color} ${matchConfig.borderColor}`} data-id="element-5934">
                                              {matchConfig.label}
                                            </span>
                                          </div>
                                          {/* Status overlay */}
                                          {item.status !== 'pending' && <div className={`absolute bottom-0 inset-x-0 py-1 px-2 flex items-center justify-center gap-1 text-[11px] font-medium ${item.status === 'approved' ? 'bg-emerald-500/90 text-white' : 'bg-red-500/90 text-white'}`} data-id="element-5935">
                                              {item.status === 'approved' ? <CheckCircle2 className="h-3 w-3" data-id="element-5936" /> : <XCircle className="h-3 w-3" data-id="element-5937" />}
                                              {item.status === 'approved' ? 'Approved' : 'Rejected'}
                                            </div>}
                                        </button>

                                        {/* Card content */}
                                        <div className="p-3 flex-1 flex flex-col" data-id="element-5938">
                                          <button onClick={() => {
                              setDetailItemId(item.id);
                              setPanelRejecting(false);
                              setPanelRejectFeedback('');
                            }} className="font-medium text-slate-900 text-sm hover:text-primary-600 transition-colors text-left leading-tight mb-1" data-id="element-5939">
                                            {item.optionName}
                                          </button>
                                          {item.manufacturerName && <p className="text-[11px] text-slate-400 mb-2" data-id="element-5940">
                                              {item.manufacturerName}
                                              {item.sku ? ` · ${item.sku}` : ''}
                                            </p>}
                                          {!item.manufacturerName && <div className="mb-2" data-id="element-5941" />}

                                          {/* Price + standard */}
                                          <div className="mt-auto" data-id="element-5942">
                                            {item.isStandard ? <div className="flex items-center gap-1 text-xs font-medium text-emerald-600 mb-2" data-id="element-5943">
                                                <Star className="h-3 w-3" data-id="element-5944" />{' '}
                                                Included as Standard
                                              </div> : <p className="text-base font-bold text-slate-900 mb-2" data-id="element-5945">
                                                ${item.price.toLocaleString()}
                                              </p>}

                                            {/* Actions */}
                                            {item.status === 'pending' && <div className="flex gap-1.5" data-id="element-5946">
                                                <button onClick={() => handleApprove(item.id)} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors" data-id="element-5947">
                                                  <Check className="h-3 w-3" data-id="element-5948" />{' '}
                                                  Approve
                                                </button>
                                                <button onClick={() => {
                                  setRejectingItemId(item.id);
                                  setRejectFeedback('');
                                }} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors" data-id="element-5949">
                                                  <X className="h-3 w-3" data-id="element-5950" />{' '}
                                                  Reject
                                                </button>
                                              </div>}
                                            {item.status === 'approved' && <button onClick={() => handleUndo(item.id)} className="w-full text-center text-[11px] text-slate-400 hover:text-slate-600 underline py-1" data-id="element-5951">
                                                Undo approval
                                              </button>}
                                            {item.status === 'rejected' && <div data-id="element-5952">
                                                {item.feedback && <p className="text-[10px] text-red-600 mb-1 line-clamp-2" data-id="element-5953">
                                                    {item.feedback}
                                                  </p>}
                                                <button onClick={() => handleUndo(item.id)} className="w-full text-center text-[11px] text-slate-400 hover:text-slate-600 underline py-1" data-id="element-5954">
                                                  Undo rejection
                                                </button>
                                              </div>}
                                          </div>
                                        </div>
                                      </div>;
                      })}
                                </div>}
                            </>}
                        </div>;
              })}
                  </div>}
              </div>;
        })}

          {filteredItems.length === 0 && <div className="text-center py-16" data-id="element-5955">
              <ClipboardCheck className="h-10 w-10 text-slate-200 mx-auto mb-3" data-id="element-5956" />
              <p className="text-slate-500 font-medium" data-id="element-5957">
                No {filter !== 'all' ? filter : ''} items
              </p>
            </div>}
        </div>
      </div>

      {/* Sticky footer */}
      <div className="flex-shrink-0 border-t border-slate-200 bg-white px-6 py-4 -mx-6 -mb-6 flex items-center justify-between" data-id="element-5958">
        <div className="text-sm text-slate-500" data-id="element-5959">
          {counts.pending > 0 ? <span className="flex items-center gap-1.5" data-id="element-5960">
              <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-5961" />
              {counts.pending} item{counts.pending !== 1 ? 's' : ''} still
              pending review
            </span> : <span className="flex items-center gap-1.5 text-emerald-600" data-id="element-5962">
              <CheckCircle2 className="h-4 w-4" data-id="element-5963" />
              All items reviewed
            </span>}
        </div>
        <Button onClick={() => setSubmitModalOpen(true)} disabled={counts.pending > 0} leftIcon={<Send className="h-4 w-4" data-id="element-5965" />} data-id="element-5964">
          Submit Review ({counts.reviewed})
        </Button>
      </div>
    </div>;
}